import { create } from "zustand";
import type { ShelfCellProduct } from "./types";

export interface CartItem {
  product: ShelfCellProduct;
  quantity: number;
  locationId: string;
  /** 货架位置标识快照，如 "A3" 或 "A1-B2" */
  shelfCode: string;
  shelfQuantity: number;
}

interface CartState {
  items: CartItem[];
  addItem: (
    product: ShelfCellProduct,
    locationId: string,
    shelfCode: string,
    shelfQuantity: number,
  ) => void;
  removeItem: (productId: string, locationId: string, shelfCode: string) => void;
  updateQuantity: (
    productId: string,
    locationId: string,
    shelfCode: string,
    quantity: number,
  ) => void;
  clearCart: () => void;
  /** 切换地点时清空该地点的行（UC-SHOPSITE-002 异常分支） */
  clearLocation: (locationId: string) => void;
  totalItems: () => number;
  totalPrice: () => number;
}

/** 同一地点 + 同一货架 + 同一商品视为一行 */
function sameLine(
  item: CartItem,
  productId: string,
  locationId: string,
  shelfCode: string,
): boolean {
  return (
    item.product.id === productId &&
    item.locationId === locationId &&
    item.shelfCode === shelfCode
  );
}

/**
 * 单行可加购上限。
 *
 * 算法逐字来自基线 `cuito-cashier-web/src/stores/cartStore.ts:14-72`：
 * 组合商品只受货架摆放数量约束，普通商品同时受摆放数量与地点可用库存约束。
 */
function maxAllowed(product: ShelfCellProduct, shelfQuantity: number): number {
  return product.isBundle ? shelfQuantity : Math.min(shelfQuantity, product.availableQty);
}

export const useCartStore = create<CartState>((set, get) => ({
  items: [],

  addItem: (product, locationId, shelfCode, shelfQuantity) =>
    set((state) => {
      const existing = state.items.find((i) => sameLine(i, product.id, locationId, shelfCode));
      const limit = maxAllowed(product, shelfQuantity);
      if (existing) {
        const newQty = existing.quantity + 1;
        if (newQty > limit) return state;
        return {
          items: state.items.map((i) =>
            sameLine(i, product.id, locationId, shelfCode) ? { ...i, quantity: newQty } : i,
          ),
        };
      }
      if (limit < 1) return state;
      return {
        items: [...state.items, { product, quantity: 1, locationId, shelfCode, shelfQuantity }],
      };
    }),

  removeItem: (productId, locationId, shelfCode) =>
    set((state) => ({
      items: state.items.filter((i) => !sameLine(i, productId, locationId, shelfCode)),
    })),

  updateQuantity: (productId, locationId, shelfCode, quantity) =>
    set((state) => {
      const item = state.items.find((i) => sameLine(i, productId, locationId, shelfCode));
      if (!item) return state;
      if (quantity <= 0) {
        return {
          items: state.items.filter((i) => !sameLine(i, productId, locationId, shelfCode)),
        };
      }
      const limit = maxAllowed(item.product, item.shelfQuantity);
      if (quantity > limit) return state;
      return {
        items: state.items.map((i) =>
          sameLine(i, productId, locationId, shelfCode) ? { ...i, quantity } : i,
        ),
      };
    }),

  clearCart: () => set({ items: [] }),

  clearLocation: (locationId) =>
    set((state) => ({ items: state.items.filter((i) => i.locationId !== locationId) })),

  totalItems: () => get().items.reduce((sum, i) => sum + i.quantity, 0),

  totalPrice: () => get().items.reduce((sum, i) => sum + i.product.price * i.quantity, 0),
}));
